/**
 * The item workspace: the living document, its questions, and a run button.
 *
 * Reading view AND working view in one — open questions can be answered right
 * here (staged, same Confirm & sync), decided ones open the full question view,
 * and the green run button triggers a focused run for the item's area.
 */

import type { Item } from "../lib/item";
import type { AnswerPatch, Question } from "../lib/questions";
import { splitFrontmatter } from "../lib/questions";
import { renderMarkdown } from "../lib/markdown";
import { StateChip } from "./Chips";
import { QuestionCard } from "./QuestionCard";

/** Strip machine blocks (ask/answers) from the displayed body. */
function visibleBody(raw: string): string {
  const split = splitFrontmatter(raw);
  let body = split ? split.body : raw;
  body = body.replace(
    /<!--\s*ask:begin[^>]*-->[\s\S]*?<!--\s*ask:end\s*-->/g,
    "",
  );
  body = body.replace(
    /<!--\s*answers:begin[^>]*-->[\s\S]*?<!--\s*answers:end\s*-->/g,
    "",
  );
  return body.trim();
}

export function ItemRead({
  item,
  questions,
  repoUrl,
  stagedPaths,
  runLabel,
  onRun,
  onAnswer,
  onSnooze,
  onOpenQuestion,
  onBack,
}: {
  item: Item;
  /** All questions; the linked ones are filtered here. */
  questions: Question[];
  /** e.g. `https://github.com/owner/repo/blob/main` — optional. */
  repoUrl?: string;
  stagedPaths?: ReadonlySet<string>;
  /** Green run button label; omitted = no button. */
  runLabel?: string;
  onRun?: () => void;
  onAnswer?: (q: Question, patch: AnswerPatch) => void;
  onSnooze?: (q: Question, until: string) => void;
  onOpenQuestion?: (q: Question) => void;
  onBack: () => void;
}) {
  const linked = questions.filter((q) => q.links.includes(item.path));
  const open = linked.filter((q) => q.status === "open");
  const decided = linked.filter((q) => q.status !== "open");
  const body = visibleBody(item.raw);

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-32 pt-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <button onClick={onBack} className="text-sm text-ink-400 hover:text-ink-200">
          ‹ Back
        </button>
        {runLabel && onRun && (
          <button
            className="btn-run shrink-0 px-3 py-1.5 text-xs"
            title="Trigger a run focused on this item's area"
            onClick={onRun}
          >
            {runLabel}
          </button>
        )}
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <StateChip state={item.state} />
        <span className="chip bg-ink-850 text-ink-500">
          {item.subcategory ? `${item.domain} · ${item.subcategory}` : item.domain}
        </span>
        {item.created && (
          <span className="chip bg-ink-850 text-ink-500">created {item.created}</span>
        )}
      </div>
      <h1 className="mb-4 text-xl font-semibold leading-snug text-ink-100">
        {item.title}
      </h1>

      {open.length > 0 && (
        <div className="mb-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
            Open questions ({open.length})
          </div>
          <div className="space-y-3">
            {open.map((q) => (
              <QuestionCard
                key={q.path}
                q={q}
                staged={stagedPaths?.has(q.path) ?? false}
                onAnswer={onAnswer ? (patch) => onAnswer(q, patch) : undefined}
                onSnooze={onSnooze ? (until) => onSnooze(q, until) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {decided.length > 0 && (
        <div className="card mb-4 p-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
            Decided ({decided.length})
          </div>
          <div className="space-y-2">
            {decided.map((q) => (
              <QuestionCard
                key={q.path}
                q={q}
                mode="read"
                staged={stagedPaths?.has(q.path) ?? false}
                onOpen={onOpenQuestion ? () => onOpenQuestion(q) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      <div
        className="prose-item"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }}
      />

      {repoUrl && (
        <a
          href={`${repoUrl}/${item.path}`}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-block text-xs text-accent-soft underline underline-offset-2"
        >
          Open on GitHub ↗
        </a>
      )}
    </div>
  );
}
